import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/Input'

type NumericInputProps = {
  label?: string
  hint?: string
  error?: string
  value: number
  onValueChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  /** Valor aplicado ao sair do campo se estiver vazio. Padrão: min ou 0. */
  emptyFallback?: number
  highlighted?: boolean
}

function clamp(value: number, min?: number, max?: number) {
  let next = value
  if (min != null) next = Math.max(min, next)
  if (max != null) next = Math.min(max, next)
  return next
}

/** Input numérico que permite apagar o conteúdo e redigitar sem travar em 0. */
export function NumericInput({
  label,
  hint,
  error,
  value,
  onValueChange,
  min,
  max,
  step,
  disabled,
  emptyFallback,
  highlighted,
}: NumericInputProps) {
  const [draft, setDraft] = useState(String(value))
  const resolvedFallback = emptyFallback ?? min ?? 0

  useEffect(() => {
    setDraft(String(value))
  }, [value])

  const handleChange = (raw: string) => {
    if (raw === '' || raw === '-' || raw === '.' || raw === ',') {
      setDraft(raw.replace(',', '.'))
      return
    }

    const normalized = raw.replace(',', '.')
    if (!/^-?\d*\.?\d*$/.test(normalized)) return

    setDraft(normalized)
    const parsed = Number(normalized)
    if (!Number.isNaN(parsed)) {
      onValueChange(clamp(parsed, min, max))
    }
  }

  const handleBlur = () => {
    const normalized = draft.replace(',', '.').trim()
    if (normalized === '' || normalized === '-' || normalized === '.') {
      const next = resolvedFallback
      setDraft(String(next))
      onValueChange(next)
      return
    }

    const parsed = Number(normalized)
    if (Number.isNaN(parsed)) {
      setDraft(String(value))
      return
    }

    const next = clamp(parsed, min, max)
    setDraft(String(next))
    onValueChange(next)
  }

  return (
    <Input
      label={label}
      hint={hint}
      error={error}
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      step={step}
      min={min}
      disabled={disabled}
      highlighted={highlighted}
    />
  )
}
